/**
 * When the work happens, in the customer's words — one set of functions for
 * the proposal page and the builder (`docs/visits-billing-and-proposal.md`
 * §9.1).
 *
 * The page's words were made in two places that could not share:
 * `timingOf()` in `server/src/services/proposal.ts` and `headline()` /
 * `phraseOf()` in `client/src/quote/proposal/visit-card.tsx`. The builder's
 * Reads line has to print **exactly** what the page prints, so all three moved
 * here, unchanged except for the W-list (§9.0):
 *
 *   W1 `frequencyOf` — every N units said in words; never a weekday.
 *   W2 `openWordOf` — `until cancelled` for a repeat that never ends.
 *   W3 a relative start reads its rule (`1 business day after Diagnose is done`).
 *   W4 the second line: `Starting 7 days after Drop off is done, until the rental ends.`
 *   W5 "About N working days" only on a visit that does not repeat.
 *
 * The rule's two pieces are `startsLead` / `startsClause` from
 * `visit-when.ts` (§4.4 R and R′), never re-written here. **A production date
 * is not a promise** (proposal §6.2): nothing here prints a weekday or a day,
 * except the dates a company allowed on a visit set to Dates.
 */
import { VISIT_TRIGGER_LABELS, expectedCountFor, isOpenEnded, occurrenceDates, occurrencesOf, } from './quote-v2.js';
import { monthLabel, seasonLabel, } from './proposal.js';
import { legacyColumnsOf, startsClause, startsLead, } from './visit-when.js';
const plural = (n, word) => (n === 1 ? word : `${word}s`);
/** W1 — `Weekly` · `Bi-weekly` · `Every 3 weeks` · `Every 10 days`; null when it does not repeat. */
export function frequencyOf(when) {
    const { repeats } = when;
    if (repeats.kind !== 'every')
        return null;
    const n = Math.max(1, Math.round(repeats.every));
    if (n === 1)
        return { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' }[repeats.unit];
    if (n === 2 && repeats.unit === 'week')
        return 'Bi-weekly';
    return `Every ${n} ${repeats.unit}s`;
}
/**
 * W2 — what a visit with no number of trips says instead of one: today's
 * `while on rent` and `on call`, and `until cancelled` for a visit that starts
 * on a date or after another visit and never ends.
 */
export function openWordOf(visit) {
    if (visit.trigger !== 'date')
        return VISIT_TRIGGER_LABELS[visit.trigger].toLowerCase();
    return 'until cancelled';
}
/** The words a relative start carries onto the page, or null (§9.1 rule 7). */
function ruleOf(when, nameOf) {
    if (when.starts.kind !== 'after')
        return null;
    const names = new Map();
    const anchor = nameOf(when.starts.anchorId);
    if (anchor)
        names.set(when.starts.anchorId, anchor);
    const { ends } = when;
    const end = when.repeats.kind !== 'every' ? null
        : ends.kind === 'term_end' ? 'until the rental ends'
            : ends.kind === 'never' ? 'until cancelled'
                : ends.kind === 'date' && ends.on ? `until ${monthLabel(ends.on)}`
                    : null;
    return { lead: startsLead(when, names), clause: startsClause(when, names), end };
}
/**
 * When the work happens, said without a working date (rule 11, §6.2) —
 * today's `timingOf`, moved, with §9.1's rules 1–7.
 *
 * `phrase` is the company's own sentence, resolved by the caller; `facts` is
 * the date-free count and cadence; `mode` falls back when it has nothing to
 * say, so no component asks whether a mode is usable.
 */
export function timingOf(visit, resolved, ctx) {
    const { when } = visit;
    // Rule 1: a repeat is what the When says, not the old schedule's name.
    const recurring = when.repeats.kind !== 'none';
    const frequency = frequencyOf(when);
    const rule = ruleOf(when, ctx.nameOf);
    const relative = when.starts.kind === 'after' || when.starts.kind === 'term_end';
    const project = !recurring && when.lengthDays > 1;
    // Rule 3: the facts, in this order.
    const parts = [];
    if (visit.trigger === 'on_call') {
        parts.push(VISIT_TRIGGER_LABELS.on_call);
    }
    else if (recurring) {
        if (frequency)
            parts.push(frequency);
        if (visit.openEnded)
            parts.push(openWordOf(visit));
        else
            parts.push(`${expectedCountFor(visit)} ${plural(expectedCountFor(visit), 'visit')}`);
    }
    else if (project) {
        parts.push(`about ${when.lengthDays} working days`);
    }
    else if (rule) {
        parts.push(rule.lead);
    }
    else if (when.starts.kind === 'term_end') {
        parts.push('When the rental ends');
    }
    else {
        parts.push('One visit');
    }
    /*
     * The season — the one thing a planning date may say — and never for a
     * visit whose months hang on another visit's planning date (rule 5, D10).
     * Specific dates have their own first and last.
     */
    const season = !recurring || relative ? null
        : when.repeats.kind === 'dates'
            ? seasonLabel(when.repeats.dates[0] ?? null, when.repeats.dates.at(-1) ?? null)
            : seasonLabel(visit.startsOn, visit.endsOn);
    // Rule 6: real dates only where both yeses were given and it starts on a date.
    const dates = resolved.mode === 'dates' && resolved.datesAllowed && recurring
        && when.starts.kind === 'date'
        ? occurrenceDates(visit).filter((one) => one !== null)
        : [];
    const mode = !recurring ? 'count'
        : relative ? 'frequency'
            : resolved.mode === 'dates' && dates.length === 0 ? (season ? 'season' : 'frequency')
                : resolved.mode === 'season' && !season ? 'frequency'
                    : resolved.mode;
    return {
        mode,
        phrase: resolved.phrase,
        facts: parts.join(' · '),
        frequency,
        season,
        dates,
        // Rule 4: a project is a visit that does not repeat and runs more than a day.
        project: project ? { days: when.lengthDays, targetMonth: monthLabel(visit.targetOn) } : null,
        rule,
    };
}
/** The headline on the visit band: "Weekly, October to early December", "One visit" — today's, moved. */
export function timingHeadline(visit) {
    const timing = visit.timing;
    if (visit.count.openEnded && timing.frequency)
        return timing.frequency;
    if (timing.project)
        return timing.project.days === 1 ? 'One day' : 'One project';
    if (timing.mode === 'season' && timing.season && timing.frequency) {
        return `${timing.frequency}, ${timing.season.label}`;
    }
    if (timing.mode === 'season' && timing.season)
        return timing.season.label;
    if (timing.mode === 'dates' && timing.dates.length > 0) {
        return timing.frequency ?? `${timing.dates.length} visits on set dates`;
    }
    if (timing.mode === 'frequency' && timing.frequency)
        return timing.frequency;
    /* `count` and anything whose mode had nothing to say: the facts. */
    return timing.facts || timing.frequency || 'One visit';
}
/**
 * The line under the headline — today's `phraseOf()` plus W4: how long a
 * project runs and when it starts, or when a repeat that follows another visit
 * starts and stops, then the company's own sentence, never changed or dropped.
 */
export function timingDetail(visit, options = {}) {
    const { project, rule, phrase, frequency } = visit.timing;
    const own = options.withPhrase === false ? '' : phrase;
    if (project) {
        const days = `About ${project.days} working ${project.days === 1 ? 'day' : 'days'}`;
        const when = rule ? `, starting ${rule.clause}`
            : project.targetMonth ? `, planned for ${project.targetMonth}` : '';
        return [`${days}${when}.`, own].filter(Boolean).join(' ');
    }
    if (rule && frequency) {
        return [`Starting ${rule.clause}${rule.end ? `, ${rule.end}` : ''}.`, own].filter(Boolean).join(' ');
    }
    return own;
}
/** A full stop on the end unless there is one — the builder's Reads line only. */
export function ensurePeriod(text) {
    return /[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`;
}
/** What the builder's Reads line says the page will print: headline, then the detail. */
export function onTheProposal(visit) {
    const detail = timingDetail(visit, { withPhrase: false });
    return [ensurePeriod(timingHeadline(visit)), detail].filter(Boolean).join(' ');
}
/**
 * A visit as it would read with a different When — the popover's draft,
 * before Done. The old columns are filled the way the one writer fills them
 * (`legacyColumnsOf`), so the Reads line's "On the proposal" is worked out
 * from exactly what Done will store rather than from a guess beside it.
 */
export function visitWithWhen(visit, when, resolved) {
    const legacy = legacyColumnsOf(when, resolved, visit.qty);
    const columns = {
        qty: legacy.qty,
        schedule: legacy.schedule_kind,
        startsOn: legacy.starts_on,
        endsOn: legacy.ends_on,
        targetOn: legacy.target_on,
        durationDays: legacy.duration_days,
        trigger: legacy.trigger,
    };
    const shape = { ...columns, repeatKind: when.repeats.kind, endsKind: when.ends.kind };
    const openEnded = isOpenEnded(shape);
    return {
        ...visit,
        ...columns,
        when,
        resolved,
        openEnded,
        occurrences: openEnded ? Number.POSITIVE_INFINITY : (resolved.occurrences ?? occurrencesOf(shape)),
    };
}
/**
 * The popover's **On the proposal** line for a draft When (§4.5): what the
 * page prints for this visit in the quote's timing mode — headline, then the
 * rule or project sentence — without the company's own phrase.
 */
export function proposalLineOf(visit, settings, ctx) {
    const timing = timingOf(visit, { mode: settings.mode, phrase: '', datesAllowed: settings.datesAllowed }, ctx);
    return onTheProposal({
        timing,
        count: { n: visit.openEnded ? null : expectedCountFor(visit), unit: 'visits', openEnded: visit.openEnded },
    });
}
//# sourceMappingURL=proposal-timing.js.map